import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/daglmkhtto0ceqqnhuv0";
const interests = ["影视剧", "综艺", "音乐", "商业", "体坛", "都想看"];

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [formError, setFormError] = useState("");

  const resetStatus = () => {
    if (status !== "idle") setStatus("idle");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 反垃圾校验：蜜罐字段被填则静默当作成功，不发请求
    const honeypot = String(data.get("company_alt") || "").trim();
    if (honeypot) {
      form.reset();
      setStatus("ok");
      return;
    }

    const nickname = String(data.get("nickname") || "");
    const email = String(data.get("email") || "").trim();
    const category = String(data.get("category") || "");
    const message = String(data.get("message") || "");

    const payload = new URLSearchParams();
    if (nickname) payload.set("nickname", nickname);
    if (email) payload.set("email", email);
    if (category) payload.set("category", category);
    if (message) payload.set("message", message);

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (parsed && "message" in parsed ? String((parsed as { message?: unknown }).message || "") : "") || responseText;

      const isSpam = /spam|form data is spam/i.test(String(serverMsg));
      const okCode = parsed?.code === "OK" || response.ok;

      if (response.ok && okCode && !isSpam) {
        form.reset();
        setStatus("ok");
        setFormError("");
      } else {
        setStatus("error");
        setFormError(isSpam ? "内容未通过校验，请稍后再试。" : String(serverMsg || "提交失败，请稍后再试。"));
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-foreground-950 py-16 text-background-50 md:py-24">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary-500/20 blur-3xl"></div>
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-accent-500/20 blur-3xl"></div>

      <div className="relative mx-auto grid max-w-6xl gap-10 px-4 md:px-6 lg:grid-cols-2">
        <div>
          <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-accent-500/20 px-3 py-1 text-xs font-bold text-accent-200 ring-1 ring-accent-400/30">
            <i className="ri-mail-send-line"></i> 投稿与订阅
          </span>
          <h2 className="font-heading text-2xl font-black leading-snug md:text-4xl">
            有瓜想爆、有话想说？
            <br />
            来找我们聊一聊
          </h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-background-200/90 md:text-base">
            发现了我们遗漏的优质热点？有想看的专题方向？填下表单告诉我们。订阅「本周瓜报」，抢先掌握下一波热点节奏。
          </p>

          <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-background-200/20 bg-background-50/[0.06] p-5">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-mail-line"></i>
              </span>
              <div>
                <p className="text-xs text-background-300">投稿与合作邮箱</p>
                <a href="mailto:hello@melons.example.com" className="text-sm font-semibold text-background-50 hover:text-accent-300">
                  hello@melons.example.com
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-background-50">
                <i className="ri-time-line"></i>
              </span>
              <div>
                <p className="text-xs text-background-300">反馈时效</p>
                <p className="text-sm font-semibold text-background-50">通常 1–2 个工作日内回复</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-background-200/20 bg-background-50 p-6 text-foreground-900 shadow-2xl md:p-8">
          <h3 className="font-heading text-lg font-black">订阅本周瓜报 / 留言</h3>
          <p className="mt-1 text-xs text-foreground-500">提交即代表同意我们按需与你取得联系。</p>

          {status === "ok" && (
            <div className="mt-5 flex items-start gap-3 rounded-xl bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
              <i className="ri-checkbox-circle-fill mt-0.5 text-lg"></i>
              <span>已收到你的消息，感谢支持！我们会尽快处理并联系你。</span>
            </div>
          )}
          {status === "error" && (
            <div className="mt-5 flex items-start gap-3 rounded-xl bg-accent-100 px-4 py-3 text-sm text-accent-800">
              <i className="ri-error-warning-fill mt-0.5 text-lg"></i>
              <span>{formError || "提交失败，请稍后再试。"}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} data-readdy-form className="mt-5 grid gap-4 sm:grid-cols-2" onChange={resetStatus}>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="nickname" className="text-xs font-semibold text-foreground-700">怎么称呼你</label>
              <input
                id="nickname"
                name="nickname"
                type="text"
                placeholder="你的昵称"
                className="rounded-lg border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="contact-email" className="text-xs font-semibold text-foreground-700">常用邮箱 *</label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                placeholder="you@example.com"
                className="rounded-lg border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label htmlFor="category" className="text-xs font-semibold text-foreground-700">最常关注的方向</label>
              <select
                id="category"
                name="category"
                defaultValue="都想看"
                className="cursor-pointer rounded-lg border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400"
              >
                {interests.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label htmlFor="message" className="text-xs font-semibold text-foreground-700">想看的专题 / 投稿线索（可选）</label>
              <textarea
                id="message"
                name="message"
                rows={3}
                maxLength={500}
                placeholder="写下你关注的专题方向，或你发现的高质量热点线索……"
                className="resize-none rounded-lg border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
              ></textarea>
              <span className="text-right text-[11px] text-foreground-400">最多 500 字</span>
            </div>

            <div className="contact-note" aria-hidden="true">
              <label htmlFor="company-alt">公司名称</label>
              <input id="company-alt" name="company_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>

            <div className="sm:col-span-2">
              <button
                type="submit"
                className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
              >
                提交并订阅瓜报
                <i className="ri-send-plane-fill"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}